import React from 'react';
import type { PublicRequestableEvent } from '@/lib/rta-api.ts';
import styles from '../Booking/EventPage.module.css';

/** Props for the requestable-zone cards rendered inside the shared Select Type list. */
export interface RequestableZoneOptionsProps {
  /** Canonical requestable rows for the active event. */
  rows: readonly PublicRequestableEvent[];
  /** Currently active requestable inventory id, when any. */
  selectedInventoryId: string | null;
  /** Select one canonical server-returned row. */
  onSelect: (row: PublicRequestableEvent) => void;
}

/**
 * Render the active event's requestable (Request to Attend) zones as cards
 * inside the shared "Select Type" list — visually identical to paid
 * access-type cards, tagged with a Request to Attend descriptor. Returns a
 * fragment (no section wrapper) so the RTA zones occupy the same selection
 * group as paid types instead of a separate section.
 *
 * @param props - Canonical rows, current selection, and selection callback.
 * @returns Accessible pressed-state cards, one per requestable inventory.
 */
export function RequestableZoneOptions({
  rows,
  selectedInventoryId,
  onSelect,
}: RequestableZoneOptionsProps): React.ReactElement | null {
  if (rows.length === 0) return null;

  return (
    <>
      {rows.map((row) => {
        const isSelected = selectedInventoryId === row.event_inventory_id;
        return (
          <button
            key={row.event_inventory_id}
            className={isSelected ? styles.accessTypeCardSelected : styles.accessTypeCard}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(row)}
          >
            <div className={styles.accessTypeName}>{row.inventory_name ?? 'Request to Attend'}</div>
            <div className={styles.accessTypeInfo}>Request to Attend</div>
          </button>
        );
      })}
    </>
  );
}
